// modules/auth/Login.tsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../core/services/firebase/config';
import { useAuth, ADMIN_UIDS } from './AuthContext';
import { Lock, Mail, ArrowRight, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { Logo } from '../../components/Logo';

export const Login: React.FC = () => {
  const { tenant, user, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();

  // Auto-redirect if already logged in
  React.useEffect(() => {
    if (!authLoading && user) {
      if (ADMIN_UIDS.includes(user.uid)) {
        navigate('/admin/dashboard');
      } else {
        navigate('/admin/profile');
      }
    }
  }, [user, authLoading, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (ADMIN_UIDS.includes(user.uid)) {
        navigate('/admin/dashboard');
      } else {
        navigate('/admin/profile');
      }
    } catch (err: any) {
      console.error(err);
      if (err.code === 'auth/invalid-credential') {
        setError('Email ou senha incorretos.');
      } else {
        setError('Ocorreu um erro ao fazer login.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-midnight p-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-glow-mesh-dark opacity-30"></div>
      
      <div className="max-w-md w-full bg-brand-charcoal/30 border border-white/10 rounded-[2.5rem] shadow-2xl overflow-hidden relative z-10 backdrop-blur-3xl animate-in fade-in zoom-in duration-700">
        {/* Header */}
        <div className="p-10 text-center border-b border-white/5 bg-white/[0.02]">
          <div className="flex justify-center mb-8">
             <Logo width={180} />
          </div>
          <h2 className="text-3xl font-black text-white font-display uppercase tracking-tight">Bem-vindo</h2>
          <p className="text-white/40 mt-2 text-xs font-black uppercase tracking-widest">A Rede Social da Pesca Esportiva</p>
        </div>

        {/* Form */}
        <div className="p-10">
          {error && (
            <div className="mb-8 bg-red-500/10 border border-red-500/20 p-4 rounded-xl flex items-start gap-3">
              <AlertCircle className="text-red-500 mt-0.5" size={18} />
              <p className="text-xs font-bold text-red-400 uppercase tracking-wide">{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-8">
            <div className="space-y-3">
              <label className="block text-[10px] font-black text-white/30 uppercase tracking-[0.2em] px-1">Email</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                  <Mail size={18} className="text-white/20 group-focus-within:text-brand-gold transition-colors" />
                </div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-14 pr-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-white focus:ring-4 focus:ring-brand-gold/10 outline-none transition-all placeholder:text-white/10 font-medium"
                  placeholder="exemplo@nossapesca.com"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center px-1">
                <label className="block text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">Senha</label>
                <a href="#" className="text-[10px] font-black text-brand-gold/40 hover:text-brand-gold uppercase tracking-widest transition-colors">Esqueci</a>
              </div>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                  <Lock size={18} className="text-white/20 group-focus-within:text-brand-gold transition-colors" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-14 pr-12 py-4 bg-white/5 border border-white/10 rounded-2xl text-white focus:ring-4 focus:ring-brand-gold/10 outline-none transition-all placeholder:text-white/10 font-medium"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-white/20 hover:text-white transition-colors cursor-pointer"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-gold text-brand-midnight font-black py-5 px-8 rounded-2xl shadow-glow-gold hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-widest text-sm"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <>
                  Entrar
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="p-8 text-center bg-white/[0.02] border-t border-white/5 space-y-3">
          <p className="text-[9px] font-black text-white/20 uppercase tracking-[0.3em]">
            Powered by <span className="text-white/40">Nossa Pesca Engine</span>
          </p>
          <a 
            href="/app" 
            className="inline-block text-[10px] font-bold text-white/30 hover:text-white/60 uppercase tracking-widest transition-colors"
          >
            ← Central de Acesso
          </a>
        </div>
      </div>
    </div>
  );
};
